import { z } from 'zod';
import { deviceSourceSchema, photoSourceSchema } from './commands.js';
/**
 * REST API contract (request/response DTOs) shared between the API and both
 * frontends. All endpoints live under `/api`.
 */
// ── Sessions ────────────────────────────────────────────────────────────────
export const sessionStatusSchema = z.enum([
    /** Created, nobody connected to the room yet (code/link still valid). */
    'wartend',
    /** Customer (and possibly staff) connected. */
    'aktiv',
    /** Ended; filed away, photos remain until loeschenAm. */
    'beendet',
]);
export const sessionOriginSchema = z.enum(['kunde', 'mitarbeiter']);
export const sessionDtoSchema = z.object({
    id: z.string(),
    status: sessionStatusSchema,
    origin: sessionOriginSchema,
    kennzeichen: z.string().nullable(),
    auftragsnummer: z.string().nullable(),
    notizen: z.string().nullable(),
    /** Filing name: `Auftragsnummer_Kennzeichen_Datum` (OHNE-AUFTRAG when empty). */
    ablageName: z.string(),
    photoCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    endedAt: z.string().datetime().nullable(),
    loeschenAm: z.string().datetime().nullable(),
});
export const photoDtoSchema = z.object({
    id: z.string(),
    sessionId: z.string(),
    source: photoSourceSchema,
    device: deviceSourceSchema,
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    sizeBytes: z.number().int().positive(),
    sha256: z.string().length(64),
    contentType: z.string(),
    takenAt: z.string().datetime(),
    /** Short-lived presigned GET URL for display; only filled for staff. */
    url: z.string().nullable(),
});
// ── Auth (staff) ────────────────────────────────────────────────────────────
export const loginRequestSchema = z.object({
    username: z.string().min(1).max(100),
    password: z.string().min(1).max(200),
});
export const userDtoSchema = z.object({
    id: z.string(),
    username: z.string(),
    displayName: z.string(),
    role: z.enum(['admin', 'mitarbeiter']),
});
// ── Session lifecycle ───────────────────────────────────────────────────────
/** Staff creates a session (link/SMS path). Response contains code + link. */
export const createSessionResponseSchema = z.object({
    session: sessionDtoSchema,
    /** 6-digit one-time code, TTL 15 minutes. */
    code: z.string().length(6),
    /** Absolute customer URL containing the signed link token. */
    linkUrl: z.string(),
});
/** Tolerant license plate rule: uppercase letters/digits/umlauts, spaces and
 * hyphens, 2–20 chars. Foreign plates must pass. */
export const kennzeichenSchema = z
    .string()
    .trim()
    .min(2)
    .max(20)
    .regex(/^[A-Za-zÄÖÜäöü0-9 \-·.]+$/, 'Ungültiges Kennzeichen');
/** Customer starts a session from the landing page (code path). */
export const customerStartRequestSchema = z.object({
    kennzeichen: kennzeichenSchema,
    /** Consent checkbox is mandatory before any camera access. */
    consent: z.literal(true),
});
export const customerStartResponseSchema = z.object({
    sessionId: z.string(),
    /** Code the customer reads out to the employee on the phone. */
    code: z.string().length(6),
    /** Bearer token scoped to this session (customer role). */
    customerToken: z.string(),
});
/** Customer joins an existing staff-created session via SMS link token or code. */
export const customerJoinRequestSchema = z.object({
    kennzeichen: kennzeichenSchema,
    consent: z.literal(true),
    /** Exactly one of linkToken / code must be provided. */
    linkToken: z.string().optional(),
    code: z.string().length(6).optional(),
});
export const customerJoinResponseSchema = z.object({
    sessionId: z.string(),
    customerToken: z.string(),
});
/** Staff claims a customer-started session by entering its code. */
export const staffJoinByCodeRequestSchema = z.object({
    code: z.string().length(6),
});
export const staffJoinByCodeResponseSchema = z.object({
    session: sessionDtoSchema,
});
export const updateSessionRequestSchema = z.object({
    auftragsnummer: z.string().max(100).nullable().optional(),
    notizen: z.string().max(10_000).nullable().optional(),
});
/** Staff reports a HINT sent to the customer so it lands in the audit log.
 * Same length limit as the HINT control message. */
export const hintLogRequestSchema = z.object({
    text: z.string().trim().min(1).max(200),
});
// ── LiveKit ─────────────────────────────────────────────────────────────────
export const livekitTokenResponseSchema = z.object({
    /** WebSocket URL of the LiveKit server (public). */
    url: z.string(),
    token: z.string(),
    /** Room name (= session id). */
    room: z.string(),
});
// ── Photo upload pipeline ───────────────────────────────────────────────────
export const presignUploadRequestSchema = z.object({
    contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    source: photoSourceSchema,
    device: deviceSourceSchema.default('handy'),
});
export const presignUploadResponseSchema = z.object({
    photoId: z.string(),
    objectKey: z.string(),
    /** Presigned PUT URL, valid for a few minutes; client uploads directly. */
    uploadUrl: z.string(),
});
export const completeUploadRequestSchema = z.object({
    photoId: z.string(),
    /** Hex SHA-256 the client computed; the server re-hashes the object and
     * rejects on mismatch. */
    sha256: z.string().length(64),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
export const completeUploadResponseSchema = z.object({
    photo: photoDtoSchema,
});
// ── Misc ────────────────────────────────────────────────────────────────────
export const configResponseSchema = z.object({
    /** Public LiveKit WebSocket URL for browser clients. */
    livekitUrl: z.string(),
    /** Configurable consent text shown next to the checkbox. */
    consentText: z.string(),
    /** Download path of the Android app for customers with an external USB
     * camera, or null when no APK is deployed — the customer page then hides
     * the link instead of offering a dead one. */
    androidApkUrl: z.string().nullable().default(null),
    /** Size of that APK in bytes (for the download hint), null when absent. */
    androidApkSizeBytes: z.number().int().positive().nullable().default(null),
    /** Impressum / privacy notice links for the customer app; null = hidden. */
    impressumUrl: z.string().nullable().default(null),
    datenschutzUrl: z.string().nullable().default(null),
});
/** Generic acknowledgement for endpoints without a payload to return. */
export const okResponseSchema = z.object({ ok: z.literal(true) });
export const sessionListQuerySchema = z.object({
    /** Filter: 'aktiv' shows wartend+aktiv, 'abgelegt' shows beendet. */
    ablage: z.enum(['aktiv', 'abgelegt']).optional(),
    /** Substring search over Kennzeichen and Auftragsnummer. */
    suche: z.string().max(100).optional(),
});
export const auditEventTypeSchema = z.enum([
    'LOGIN',
    'LOGIN_FAILED',
    'SESSION_CREATED',
    'SESSION_JOINED',
    'AUFTRAG_SET',
    'PHOTO_TAKEN',
    'PHOTO_DELETED',
    'EXPORT',
    'HINT_SENT',
    'SESSION_ENDED',
    'SESSION_DELETED',
]);
//# sourceMappingURL=dto.js.map